/**
 * The trigger for the next web modal. `nativeID` maps to the DOM `id`, making
 * the target unique even when multiple messages render the same testID.
 */
export interface WebModalReturnFocusTarget {
  nativeID: string;
  fallbackNativeID?: string;
  element: HTMLElement | null;
}

let pendingReturnFocusTarget: WebModalReturnFocusTarget | null = null;

function isFocusableElement(value: unknown): value is HTMLElement {
  return (
    value != null &&
    typeof value === "object" &&
    "focus" in value &&
    typeof value.focus === "function" &&
    "isConnected" in value
  );
}

/**
 * Captures the exact trigger before navigation moves focus into the modal.
 */
export function setWebModalReturnFocusTarget(nativeID: string, fallbackNativeID?: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const activeElement = document.activeElement;
  pendingReturnFocusTarget = {
    nativeID,
    fallbackNativeID,
    element: isFocusableElement(activeElement) ? activeElement : null,
  };
}

/**
 * Claims the trigger when the modal focus trap opens.
 */
export function claimWebModalReturnFocusTarget(): WebModalReturnFocusTarget | null {
  const target = pendingReturnFocusTarget;
  pendingReturnFocusTarget = null;
  return target;
}

/**
 * Restores focus synchronously after the modal focus trap releases `inert`.
 */
export function restoreWebModalReturnFocusTarget(
  target: WebModalReturnFocusTarget | null | undefined,
): void {
  if (!target || typeof document === "undefined") {
    return;
  }

  const primaryElement =
    target.element?.isConnected === true
      ? target.element
      : document.getElementById(target.nativeID);
  const fallbackElement =
    !primaryElement && target.fallbackNativeID
      ? document.getElementById(target.fallbackNativeID)
      : null;
  if (fallbackElement && !fallbackElement.hasAttribute("tabindex")) {
    fallbackElement.setAttribute("tabindex", "-1");
  }
  (primaryElement ?? fallbackElement)?.focus();
}

export function clearWebModalReturnFocusTarget(): void {
  pendingReturnFocusTarget = null;
}
